import { useContext, useEffect, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';

import {
  useLazyGetClientServiceCategoriesForFilterQuery,
  useLazyGetClientServicesQuery,
  useUpdateEmployeeClientServicesMutation,
  type SalaryFormat,
} from '@app/api/ClientServices';
import { SUCCESS, WARNING } from '@app/constants';
import { useMemoizeObject, useUserPrint } from '@app/hooks';
import { NotificationContext } from '@app/providers';
import type { NTabsNotificationProvider } from '@app/providers/TabsNotificationProvider/TabsNotificationProvider.types';
import { handleCatchError } from '@app/services';
import type { NClientServices } from '@app/types/ClientServices';
import type { NEmployees } from '@app/types/Employees';
import type { NUsers } from '@app/types/Users';

import {
  COPYING_SERVICES_FROM_EMPLOYEE_PARAM,
  type ServicesLoadState,
} from '@pages/Employees/EditEmployee/data/copyingServices';
import type { NEditEmployee } from '@pages/Employees/EditEmployee/EditEmployee.types';

import type { BaseOptionType } from 'antd/es/select';

export const useClientServicesTab = (
  employee: NUsers.User | undefined,
  allServices: NEmployees.ModelClientService[],
  setAllServices: (value: NEmployees.ModelClientService[]) => void,
  setNewData: NTabsNotificationProvider.Context['setNewData'],
  getEmployeeData: () => Promise<void>,
  servicesLoadState: ServicesLoadState
) => {
  const { activeBranchId, userId, database } = useUserPrint();
  const { showNotification } = useContext(NotificationContext);
  const [getClientServiceCategoriesForFilter] =
    useLazyGetClientServiceCategoriesForFilterQuery();
  const [getClientServices] = useLazyGetClientServicesQuery();
  const [updateEmployeeClientServices] =
    useUpdateEmployeeClientServicesMutation();
  const [servicesToShow, setServicesToShow] = useState<
    NEmployees.ModelClientServiceWithOptionalProps[]
  >([]);
  const [isLoading, setLoading] = useState(false);
  const [allCategories, setAllCategories] = useState<
    Array<NClientServices.Category>
  >([]);
  const [selectedCategories, setSelectedCategories] = useState<
    BaseOptionType[] | null
  >(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [allModalServices, setAllModalServices] = useState<
    NEmployees.ModelClientService[]
  >([]);
  const [processedModalServices, setProcessedModalServices] = useState<
    NEditEmployee.ProcessedModalService[]
  >([]);
  const [addingFromModalServices, setAddingFromModalServices] = useState<
    number[]
  >([]);
  const [changedServices, setChangedServices] = useState<number[]>([]);
  const [employeeSalaryModalOpen, setEmployeeSalaryModalOpen] = useState(false);
  const [isCopyModalOpen, setIsCopyModalOpen] = useState(false);

  const [searchParams, setSearchParams] = useSearchParams();
  const isCopyingServices = servicesLoadState.isCopying;
  // Пока услуги не загружены (или загрузка упала), в таблице прежний список —
  // копия, отменённая копия или пустой, поэтому сохранять его нельзя
  const isSavingBlocked = servicesLoadState.status !== 'success';

  // После сохранения скопированных услуг (или отмены копирования) убираем
  // признак копирования — страница перезапросит услуги текущего исполнителя
  const finishCopyingServices = useCallback(() => {
    const nextSearchParams = new URLSearchParams(searchParams);

    nextSearchParams.delete(COPYING_SERVICES_FROM_EMPLOYEE_PARAM);
    setSearchParams(nextSearchParams);
  }, [searchParams, setSearchParams]);

  const memoizedAllServices = useMemoizeObject(allServices);
  const memoizedSelectedCategories = useMemoizeObject(selectedCategories);
  const memoizedEmployee = useMemoizeObject(employee);

  const [allEmployeeServices, setAllEmployeeServices] = useState<
    NEmployees.ModelClientServiceWithOptionalProps[]
  >([]);

  const getInitialData = useCallback(async () => {
    if (memoizedEmployee) {
      try {
        setLoading(true);

        const initialServices =
          memoizedAllServices?.map((item) => {
            return { ...item, isAddingFromModal: false };
          }) || [];

        setServicesToShow(initialServices);
        setAllEmployeeServices(initialServices);

        const categories = await getClientServiceCategoriesForFilter({
          ActiveBranchId: activeBranchId,
          logs: { isLogged: true, userId, database },
        }).unwrap();

        const services = await getClientServices({
          ActiveBranchId: activeBranchId,
        }).unwrap();

        setAllModalServices(services);

        const extractedCategoryIds = new Set(
          services.map(({ CategoryId }) => CategoryId)
        );
        const extractedCategories: NClientServices.ModelClientServiceCategory[] =
          [];

        extractedCategoryIds.forEach((categoryId) => {
          return categories.forEach((category) => {
            const { Id: _categoryId } = category;

            if (categoryId === _categoryId) {
              return extractedCategories.push(category);
            }
          });
        });

        setAllCategories(extractedCategories);
      } catch (error) {
        showNotification(handleCatchError(error), WARNING);
      } finally {
        setLoading(false);
      }
    }
  }, [
    activeBranchId,
    userId,
    database,
    memoizedAllServices,
    memoizedEmployee,
    showNotification,
    getClientServiceCategoriesForFilter,
    getClientServices,
  ]);

  useEffect(() => {
    getInitialData();
  }, [getInitialData]);

  useEffect(() => {
    const uniqueCategories = Array.from(
      new Set(
        allModalServices.map(
          (service: NEmployees.ModelClientService) => service.Category
        )
      )
    );
    const allServicesIds = allServices.map(
      (service: NEmployees.ModelClientService) => service.Id
    );

    const filledProcessedModalServices = uniqueCategories.map((category) => {
      return {
        name: category,
        selected: false,
        items: allModalServices
          .filter(
            (item: NEmployees.ModelClientService) => item.Category === category
          )
          .map((item) => {
            return {
              ...item,
              Selected: allServicesIds.includes(item.Id),
            };
          }),
      };
    });
    setProcessedModalServices(filledProcessedModalServices);
  }, [allServices, allModalServices]);

  useEffect(() => {
    const handledMemoizedAllServices = memoizedAllServices || [];

    if (
      !memoizedSelectedCategories?.length ||
      memoizedSelectedCategories?.length === allCategories.length
    ) {
      setServicesToShow(handledMemoizedAllServices);
    } else {
      setServicesToShow(
        handledMemoizedAllServices.filter((service) =>
          memoizedSelectedCategories.find(
            (category) => service.CategoryId === category.value
          )
        )
      );
    }
  }, [allCategories.length, memoizedAllServices, memoizedSelectedCategories]);

  useEffect(() => {
    setAllEmployeeServices((prev) => {
      const updatedServices = [...prev];

      servicesToShow.forEach((service) => {
        const index = updatedServices.findIndex((s) => s.Id === service.Id);
        if (index !== -1) {
          updatedServices[index] = service;
        } else {
          updatedServices.push(service);
        }
      });

      return updatedServices;
    });
  }, [servicesToShow]);

  const deleteCategory = (id: number) => {
    const deletedService = allModalServices.find(
      (service) => service.Id === id
    );

    setServicesToShow((prev) => [
      ...prev.filter((service) => service.Id !== id),
    ]);

    setAllEmployeeServices((prev) => [
      ...prev.filter((service) => service.Id !== id),
    ]);

    setProcessedModalServices((prev) => [
      ...prev.map((service) => {
        if (service.name === deletedService?.Category) {
          const items = service.items?.map((item) => {
            if (item.Id === id) {
              return {
                ...item,
                Selected: false,
              };
            } else return item;
          });

          return {
            ...service,
            items,
            selected: false,
          };
        } else {
          return service;
        }
      }),
    ]);
  };

  const handleOk = () => {
    setLoading(true);

    const selectedServices = processedModalServices
      .flatMap((service) => service.items.filter((item) => item.Selected))
      .map((item) => {
        const currentServicesToShow = servicesToShow.find(
          (service) => service.Id === item.Id
        );
        return {
          ...(currentServicesToShow ? currentServicesToShow : item),
          isAddingFromModal: currentServicesToShow
            ? currentServicesToShow.isAddingFromModal
            : true,
        };
      });

    setServicesToShow(selectedServices);
    setAllEmployeeServices((prev) => {
      const newServices = [...prev];

      selectedServices.forEach((service) => {
        const index = newServices.findIndex((s) => s.Id === service.Id);
        if (index !== -1) {
          newServices[index] = service;
        } else {
          newServices.push({ ...service, isAddingFromModal: true });
        }
      });

      return newServices;
    });

    handleModalClose(selectedServices);
    setLoading(false);
  };

  const onHourSelect = (value: number, id: number | undefined) => {
    setServicesToShow((prev) => [
      ...prev.map((service) => {
        if (service.Id === id) {
          setChangedServices((prev) => [...prev, id]);

          const hours = Math.floor(service.Duration / 60);
          const minutes = service.Duration - hours * 60;

          return {
            ...service,
            Duration: value * 60 + minutes,
          };
        } else {
          return service;
        }
      }),
    ]);

    setAllEmployeeServices((prev) => [
      ...prev.map((service) => {
        if (service.Id === id) {
          const hours = Math.floor(service.Duration / 60);
          const minutes = service.Duration - hours * 60;

          return {
            ...service,
            Duration: value * 60 + minutes,
          };
        } else {
          return service;
        }
      }),
    ]);
  };

  const onMinuteSelect = (value: number, id?: number) => {
    setServicesToShow((prev) => [
      ...prev.map((service) => {
        if (service.Id === id) {
          setChangedServices((prev) => [...prev, id]);

          const hours = Math.floor(service.Duration / 60);

          return {
            ...service,
            Duration: hours * 60 + value,
          };
        } else {
          return service;
        }
      }),
    ]);

    setAllEmployeeServices((prev) => [
      ...prev.map((service) => {
        if (service.Id === id) {
          const hours = Math.floor(service.Duration / 60);

          return {
            ...service,
            Duration: hours * 60 + value,
          };
        } else {
          return service;
        }
      }),
    ]);
  };

  const onSalaryTypeSelect = (value: SalaryFormat, id?: number) => {
    setServicesToShow((prev) => [
      ...prev.map((service) => {
        if (service.Id === id) {
          setChangedServices((prev) => [...prev, id]);

          return {
            ...service,
            SalaryFormat: value,
          };
        } else {
          return service;
        }
      }),
    ]);

    setAllEmployeeServices((prev) => [
      ...prev.map((service) => {
        if (service.Id === id) {
          return {
            ...service,
            SalaryFormat: value,
          };
        } else {
          return service;
        }
      }),
    ]);
  };

  const onSalaryValueChange = (value: string, id?: number) => {
    setServicesToShow((prev) => [
      ...prev.map((service) => {
        if (service.Id === id) {
          setChangedServices((prev) => [...prev, id]);

          return {
            ...service,
            SalaryAmount: Number(value),
          };
        } else {
          return service;
        }
      }),
    ]);

    setAllEmployeeServices((prev) => [
      ...prev.map((service) => {
        if (service.Id === id) {
          return {
            ...service,
            SalaryAmount: Number(value),
          };
        } else {
          return service;
        }
      }),
    ]);
  };

  const handleAddServicesSalaryChange = async (
    value: string,
    salaryType: SalaryFormat
  ) => {
    if (employee?.id && !isSavingBlocked) {
      setLoading(true);

      try {
        await updateEmployeeClientServices({
          ActiveBranchId: activeBranchId,
          employeeId: employee.id,
          data: allEmployeeServices.map(({ Id, Duration }) => ({
            ServiceId: Id,
            Duration: Duration,
            SalaryFormat: salaryType,
            SalaryAmount: Number(value),
          })),
        }).unwrap();

        if (isCopyingServices) {
          finishCopyingServices();
        } else {
          await getInitialData();
          await getEmployeeData();
        }

        setAddingFromModalServices([]);
        setChangedServices([]);
        showNotification('Услуги сотрудника обновлены успешно', SUCCESS);
      } catch (error) {
        showNotification(handleCatchError(error));
      } finally {
        setLoading(false);
      }
    }
  };

  const transformMinutes = (value: number) => {
    const hours = Math.floor(value / 60);
    const minutesFormatted = (value % 60).toString().padStart(2, '0');
    const timeFormatted = `${hours} ч ${minutesFormatted} мин`;
    return timeFormatted;
  };

  const handleServiceModalCheckboxClick = (
    checked: boolean,
    id: number,
    service: NEditEmployee.ProcessedModalService
  ) => {
    setProcessedModalServices((prev) =>
      setServiceModalCheckboxClickData({
        prev,
        service,
        id,
        checked,
      })
    );
  };

  const setServiceModalCheckboxClickData: NEditEmployee.SetServiceModalCheckboxClickData =
    ({ prev, service, id, checked }) => {
      return [
        ...prev.map((item) => {
          if (item.name === service.name) {
            const updatedItemsList = item.items.map((element) => {
              if (element.Id === id) {
                return {
                  ...element,
                  Selected: checked,
                };
              } else {
                return element;
              }
            });
            return {
              ...item,
              selected:
                updatedItemsList.filter((element) => element.Selected)
                  .length ===
                prev.filter((item) => item.name === service.name)[0].items
                  .length,
              items: updatedItemsList,
            };
          } else {
            return item;
          }
        }),
      ];
    };

  const serviceLabelClick = (
    e: React.MouseEvent<HTMLElement>,
    service: NEditEmployee.ProcessedModalService
  ) => {
    const { checked } = e.target as HTMLInputElement;

    setProcessedModalServices((prev) =>
      setServiceLabelClickData({
        prev,
        checked,
        service,
      })
    );
  };

  const setServiceLabelClickData: NEditEmployee.SetServiceLabelClickData = ({
    prev,
    checked,
    service,
  }) => {
    return [
      ...prev.map((item) => {
        if (item.name === service.name) {
          return {
            ...item,
            selected: checked,
            items: item.items.map((element) => {
              return {
                ...element,
                Selected: checked,
              };
            }),
          };
        } else {
          return item;
        }
      }),
    ];
  };

  const saveData = async () => {
    if (employee?.id && !isSavingBlocked) {
      setLoading(true);

      try {
        await updateEmployeeClientServices({
          ActiveBranchId: activeBranchId,
          employeeId: employee.id,
          data: allEmployeeServices.map(
            ({ Id, Duration, SalaryFormat, SalaryAmount }) => ({
              ServiceId: Id,
              Duration: Duration,
              SalaryFormat,
              SalaryAmount,
            })
          ),
        }).unwrap();

        setAddingFromModalServices([]);
        setChangedServices([]);
        setAllServices(
          allEmployeeServices.map((service) => ({
            ...service,
            isAddingFromModal: false,
            isDeletedAtClientSide: false,
          }))
        );

        if (isCopyingServices) {
          finishCopyingServices();
        }

        showNotification('Услуги сотрудника обновлены успешно', SUCCESS);
      } catch (error) {
        showNotification(handleCatchError(error));
      } finally {
        setLoading(false);
      }
    }
  };

  const handleModalClose = (
    newServicesToShow?: NEmployees.ModelClientServiceWithOptionalProps[]
  ) => {
    const newProcessedModalServices = processedModalServices.map(
      (serviceCategory) => {
        const newServiceCategoryItems = serviceCategory.items.map((item) => {
          return {
            ...item,
            Selected: Boolean(
              (newServicesToShow ? newServicesToShow : servicesToShow).find(
                ({ Id }) => item.Id === Id
              )
            ),
          };
        });

        return {
          ...serviceCategory,
          selected:
            newServiceCategoryItems.filter(({ Selected }) => Selected)
              .length === newServiceCategoryItems.length,
          items: newServiceCategoryItems,
        };
      }
    );

    setProcessedModalServices(newProcessedModalServices);
    setModalOpen(false);
  };

  return {
    servicesToShow,
    isLoading,
    allCategories,
    selectedCategories,
    setSelectedCategories,
    deleteCategory,
    modalOpen,
    setModalOpen,
    handleOk,
    allModalServices,
    onHourSelect,
    onMinuteSelect,
    transformMinutes,
    processedModalServices,
    handleServiceModalCheckboxClick,
    serviceLabelClick,
    addingFromModalServices,
    saveData,
    changedServices,
    handleModalClose,
    onSalaryTypeSelect,
    onSalaryValueChange,
    handleAddServicesSalaryChange,
    employeeSalaryModalOpen,
    setEmployeeSalaryModalOpen,
    isCopyModalOpen,
    setIsCopyModalOpen,
    isCopyingServices,
    isSavingBlocked,
    finishCopyingServices,
  };
};
